import {ICurriculumsRepository} from './ICurriculumsRepository';
import {Amplify} from 'aws-amplify';
import {
  InitWithAmplify,
  InitEnvironmentVariable,
  CurriculumsService as PikadoCurriculumsService,
} from '@PikaDoApp/npm-pikado-logic';
import {
  NewCreateCurriculumInput,
  UpdateCurriculumInput,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/curriculum/graphql/curriculum.interface';
import {getEnvironment} from '../../lib/getEnvironment';
import {CurriculumUI} from './CurriculumTypes';

// Initialize the Pikado Logic package
InitWithAmplify(Amplify);
InitEnvironmentVariable(getEnvironment());

const pikadoCurriculumsService = new PikadoCurriculumsService();

export class CurriculumsRepository implements ICurriculumsRepository {
  async getCurriculums(): Promise<Array<CurriculumUI>> {
    try {
      const curriculums = await pikadoCurriculumsService.getNewCurriculums();
      if (!curriculums) {
        return [];
      }

      const transformedCurriculums: CurriculumUI[] = curriculums.map(
        curriculum => ({
          ...curriculum,
          id: curriculum._id, // Map _id to id for UI
        }),
      );

      return transformedCurriculums;
    } catch (error: any) {
      console.error(
        '🔍 [CURRICULUM-NEW-PACKAGE] getCurriculums repository Error:',
        error,
      );
      throw new Error(error);
    }
  }

  async getCurriculumById(id: string): Promise<CurriculumUI | null> {
    try {
      const curriculum = await pikadoCurriculumsService.getCurriculumByID(id);
      if (!curriculum) {
        return null;
      }

      const transformedCurriculum: CurriculumUI = {
        ...curriculum,
        id: curriculum._id,
        createdAt: curriculum.createdAt || new Date().toISOString(),
        updatedAt: curriculum.updatedAt || new Date().toISOString(),
      };

      return transformedCurriculum;
    } catch (error: any) {
      console.error(
        '🔍 [CURRICULUM-NEW-PACKAGE] getCurriculumById repository Error:',
        error,
      );
      throw new Error(error);
    }
  }

  async createNewCurriculum(
    curriculum: NewCreateCurriculumInput,
  ): Promise<CurriculumUI> {
    try {
      const createdCurriculum =
        await pikadoCurriculumsService.addNewCurriculum(curriculum);

      if (!createdCurriculum) {
        throw new Error('Failed to create curriculum');
      }

      const transformedCurriculum: CurriculumUI = {
        ...createdCurriculum,
        id: createdCurriculum._id,
        createdAt: createdCurriculum.createdAt || new Date().toISOString(),
        updatedAt: createdCurriculum.updatedAt || new Date().toISOString(),
      };

      return transformedCurriculum;
    } catch (error: any) {
      console.error(
        '🔍 [CURRICULUM-NEW-PACKAGE] createNewCurriculum repository Error:',
        error,
      );
      throw new Error(error);
    }
  }

  async updateNewCurriculum(
    input: UpdateCurriculumInput,
  ): Promise<CurriculumUI> {
    try {
      const updatedCurriculum =
        await pikadoCurriculumsService.updateCurriculum(input);

      if (!updatedCurriculum) {
        throw new Error('Failed to update curriculum');
      }

      const transformedCurriculum: CurriculumUI = {
        ...updatedCurriculum,
        id: updatedCurriculum._id,
        createdAt: updatedCurriculum.createdAt || new Date().toISOString(),
        updatedAt: updatedCurriculum.updatedAt || new Date().toISOString(),
      };

      return transformedCurriculum;
    } catch (error: any) {
      console.error(
        '🔍 [CURRICULUM-NEW-PACKAGE] updateNewCurriculum repository Error:',
        error,
      );
      throw new Error(error);
    }
  }

  async softDeleteNewCurriculum(id: string): Promise<boolean> {
    try {
      const result = await pikadoCurriculumsService.softDeleteCurriculum(id);
      return result;
    } catch (error) {
      console.error(
        '🔍 [CURRICULUM-NEW-PACKAGE] softDeleteNewCurriculum repository Error:',
        error,
      );
      throw error instanceof Error ? error : new Error(String(error));
    }
  }
}
